import type { GameEvent } from '../schema';

/** Crisis, luck, opportunity, inertia and the late-life arc. */
export const CRISIS_EVENTS: GameEvent[] = [
  {
    id: 'crisis.recession',
    title: 'The economy turns',
    description: 'Hiring freezes, falling asset prices, and a general sense that everyone is waiting.',
    category: 'crisis',
    pools: ['pool.crisis', 'pool.finance.mid', 'pool.finance.late', 'pool.investment'],
    weight: 6,
    cooldown: 8,
    requires: [{ k: 'any', of: [{ k: 'str', path: 'marketRegime', op: '==', value: 'bust' }, { k: 'str', path: 'marketRegime', op: '==', value: 'soft' }] }],
    choices: [
      {
        id: 'defensive',
        label: 'Go defensive',
        detail: 'Cut spending, hold cash, wait it out.',
        hint: 'safe',
        tags: ['security'],
        outcomes: [
          {
            weight: 1,
            label: 'Battened down',
            narrative: 'You spent less and slept better. It cost you the recovery you did not participate in.',
            tone: 'neutral',
            effects: [
              { k: 'lifestyle', delta: -1 },
              { k: 'habit', habit: 'saving', delta: 10 },
              { k: 'stat', stat: 'stress', delta: -6 },
              { k: 'career', op: 'adjust', field: 'jobSecurity', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'opportunistic',
        label: 'Treat it as a buying opportunity',
        hint: 'risky',
        requires: [{ k: 'num', path: 'cash', op: '>=', value: 15000 }],
        showWhenLocked: true,
        tags: ['risk', 'finance'],
        outcomes: [
          {
            weight: 1,
            label: 'Bought into weakness',
            narrative: 'Nobody congratulates you for this at the time.',
            tone: 'neutral',
            effects: [
              { k: 'invest', op: 'buy', assetId: 'growth', amount: 15000 },
              { k: 'stat', stat: 'stress', delta: 10 },
              { k: 'schedule', afterYears: 5, label: 'The recovery arrived', effects: [{ k: 'invest', op: 'shock', assetId: 'growth', fraction: 0.45 }, { k: 'skill', skill: 'financial', delta: 8 }] },
            ],
          },
        ],
      },
      {
        id: 'ignore',
        label: 'Carry on as normal',
        hint: 'mixed',
        outcomes: [
          {
            weight: 1,
            label: 'Unchanged',
            narrative: 'You did not react, which is statistically among the better strategies.',
            tone: 'neutral',
            effects: [{ k: 'stat', stat: 'stress', delta: 5 }],
          },
        ],
      },
    ],
  },
  {
    id: 'crisis.bankruptcy',
    title: 'It cannot be paid',
    description:
      'The obligations exceed anything you can service. There are formal processes for this, and they all cost something permanent.',
    category: 'crisis',
    pools: ['pool.crisis'],
    weight: 0,
    priority: 10,
    choices: [
      {
        id: 'formal',
        label: 'Declare insolvency',
        hint: 'costly',
        tags: ['crisis'],
        outcomes: [
          {
            weight: 1,
            label: 'Declared bankrupt',
            narrative: 'Debts discharged, assets gone, credit finished for years. A brutal reset that is still a reset.',
            tone: 'negative',
            effects: [
              { k: 'debt', op: 'forgive', fraction: 0.9 },
              { k: 'invest', op: 'sell', assetId: 'growth', amount: 0 },
              { k: 'invest', op: 'sell', assetId: 'speculative', amount: 0 },
              { k: 'invest', op: 'sell', assetId: 'balanced', amount: 0 },
              { k: 'counter', name: 'bankruptcies', delta: 1 },
              { k: 'counter', name: 'crises', delta: 1 },
              { k: 'flag', flag: 'wasBankrupt', value: true },
              { k: 'stat', stat: 'happiness', delta: -22 },
              { k: 'stat', stat: 'stress', delta: 20 },
              { k: 'trait', trait: 'resilience', delta: 10 },
              { k: 'lifestyle', delta: -3 },
              { k: 'memory', title: 'Declared bankruptcy', tone: 'negative', category: 'crisis', weight: 10 },
              { k: 'triggerEvent', eventId: 'crisis.rebuild', afterYears: 2 },
            ],
          },
        ],
      },
      {
        id: 'negotiate',
        label: 'Negotiate with creditors',
        hint: 'mixed',
        tags: ['crisis'],
        outcomes: [
          {
            weight: 46,
            label: 'They restructured',
            narrative: 'Lower payments over longer years. Solvent, technically, for a very long time.',
            tone: 'neutral',
            favouredBy: [{ conditions: [{ k: 'skill', skill: 'communication', op: '>=', value: 55 }], multiplier: 1.7 }],
            effects: [
              { k: 'debt', op: 'forgive', fraction: 0.35 },
              { k: 'lifestyle', delta: -2 },
              { k: 'stat', stat: 'stress', delta: 14 },
              { k: 'counter', name: 'crises', delta: 1 },
            ],
          },
          {
            weight: 54,
            label: 'They refused',
            narrative: 'The process took its course regardless.',
            tone: 'negative',
            effects: [
              { k: 'debt', op: 'forgive', fraction: 0.9 },
              { k: 'invest', op: 'sell', assetId: 'growth', amount: 0 },
              { k: 'counter', name: 'bankruptcies', delta: 1 },
              { k: 'counter', name: 'crises', delta: 1 },
              { k: 'flag', flag: 'wasBankrupt', value: true },
              { k: 'stat', stat: 'happiness', delta: -20 },
              { k: 'triggerEvent', eventId: 'crisis.rebuild', afterYears: 2 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'crisis.rebuild',
    title: 'Starting from nothing',
    description: 'No assets, damaged credit, and a great deal of experience in what not to do.',
    category: 'crisis',
    pools: ['pool.recovery'],
    weight: 0,
    choices: [
      {
        id: 'steady',
        label: 'Rebuild slowly and carefully',
        hint: 'slow',
        tags: ['recovery', 'discipline'],
        outcomes: [
          {
            weight: 1,
            label: 'Rebuilt',
            narrative: 'Boring, joyless, effective. Five years later you were solvent and rather good with money.',
            tone: 'positive',
            effects: [
              { k: 'habit', habit: 'saving', delta: 20 },
              { k: 'habit', habit: 'spending', delta: -16 },
              { k: 'skill', skill: 'financial', delta: 14 },
              { k: 'trait', trait: 'discipline', delta: 8 },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'achievement', id: 'ach.rebuilt', label: 'Rebuilt from nothing', category: 'finance' },
              { k: 'schedule', afterYears: 5, label: 'Back on your feet', effects: [{ k: 'stat', stat: 'happiness', delta: 14 }, { k: 'invest', op: 'buy', assetId: 'balanced', amount: 6000 }] },
            ],
          },
        ],
      },
      {
        id: 'swing',
        label: 'Take one big swing to get back',
        hint: 'risky',
        tags: ['risk', 'desperation'],
        outcomes: [
          {
            weight: 26,
            label: 'It worked',
            narrative: 'Against every reasonable expectation, it worked.',
            tone: 'positive',
            effects: [
              { k: 'cash', amount: 85000 },
              { k: 'stat', stat: 'happiness', delta: 16 },
              { k: 'trait', trait: 'riskTolerance', delta: 10 },
              { k: 'counter', name: 'luckyBreaks', delta: 1 },
            ],
          },
          {
            weight: 74,
            label: 'It did not',
            narrative: 'The second hole was deeper than the first.',
            tone: 'negative',
            effects: [
              { k: 'debt', op: 'add', kind: 'consumer', label: 'Recovery gamble', amount: 30000, rate: 0.19, years: 6 },
              { k: 'stat', stat: 'happiness', delta: -16 },
              { k: 'stat', stat: 'stress', delta: 18 },
              { k: 'regret', op: 'add', id: 'regret.gamble', label: 'Gambled on the way back up', weight: 22 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'crisis.majorExpense',
    title: 'A genuinely large bill',
    description: 'Legal, medical, structural. Five figures, non-negotiable, and now.',
    category: 'crisis',
    pools: ['pool.crisis', 'pool.finance.mid'],
    weight: 4,
    cooldown: 7,
    requires: [{ k: 'age', min: 26 }],
    weightMods: [{ conditions: [{ k: 'flag', flag: 'insured' }], multiplier: 0.45 }],
    choices: [
      {
        id: 'pay',
        label: 'Pay it in full',
        hint: 'costly',
        outcomes: [
          {
            weight: 1,
            label: 'Paid',
            narrative: 'If there was a fund, this is why. If there was not, this is why there should have been.',
            tone: 'negative',
            effects: [
              { k: 'cashIncomeMonths', months: -6, note: 'major expense' },
              { k: 'stat', stat: 'stress', delta: 14 },
              { k: 'counter', name: 'crises', delta: 1 },
            ],
          },
        ],
      },
      {
        id: 'instalments',
        label: 'Spread it over years',
        detail: 'Smaller now, more in total.',
        hint: 'mixed',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Financed it',
            narrative: 'Manageable monthly, and a line on your statement for the next six years.',
            tone: 'neutral',
            effects: [
              { k: 'debt', op: 'add', kind: 'consumer', label: 'Instalment plan', amount: 24000, rate: 0.115, years: 6 },
              { k: 'stat', stat: 'stress', delta: 8 },
              { k: 'counter', name: 'crises', delta: 1 },
            ],
          },
        ],
      },
      {
        id: 'fight',
        label: 'Contest it',
        hint: 'risky',
        tags: ['courage'],
        outcomes: [
          {
            weight: 38,
            label: 'Reduced',
            narrative: 'Six months of letters cut it by more than half.',
            tone: 'positive',
            favouredBy: [{ conditions: [{ k: 'skill', skill: 'communication', op: '>=', value: 55 }], multiplier: 1.8 }],
            effects: [
              { k: 'cashIncomeMonths', months: -2.2, note: 'reduced settlement' },
              { k: 'stat', stat: 'stress', delta: 12 },
              { k: 'skill', skill: 'communication', delta: 4 },
            ],
          },
          {
            weight: 62,
            label: 'Upheld, with costs',
            narrative: 'You lost, and the delay added to it.',
            tone: 'negative',
            effects: [
              { k: 'cashIncomeMonths', months: -7.5, note: 'expense plus costs' },
              { k: 'stat', stat: 'stress', delta: 18 },
              { k: 'counter', name: 'crises', delta: 1 },
            ],
          },
        ],
      },
    ],
  },
];

export const OPPORTUNITY_EVENTS: GameEvent[] = [
  {
    id: 'opportunity.headhunted',
    title: 'An unsolicited approach',
    description: 'A recruiter with a specific, well-paid role and an unreasonable timeline.',
    category: 'opportunity',
    pools: ['pool.opportunity', 'pool.career.mid'],
    weight: 5,
    cooldown: 5,
    requires: [{ k: 'num', path: 'careerReputation', op: '>=', value: 48 }, { k: 'careerStatus', anyOf: ['employed', 'selfEmployed'] }],
    choices: [
      {
        id: 'take',
        label: 'Take it',
        hint: 'mixed',
        tags: ['career', 'ambition'],
        outcomes: [
          {
            weight: 66,
            label: 'A good move',
            narrative: 'More money, more scope, a manager who is competent. Rare trifecta.',
            tone: 'positive',
            effects: [
              { k: 'salary', mode: 'mult', value: 1.3 },
              { k: 'career', op: 'adjust', field: 'workload', delta: 8 },
              { k: 'career', op: 'adjust', field: 'jobSecurity', delta: -10 },
              { k: 'stat', stat: 'purpose', delta: 8 },
            ],
          },
          {
            weight: 34,
            label: 'Oversold',
            narrative: 'The role as described did not exist. You spent two years in the gap.',
            tone: 'negative',
            effects: [
              { k: 'salary', mode: 'mult', value: 1.2 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: -18 },
              { k: 'career', op: 'adjust', field: 'workload', delta: 16 },
              { k: 'burnout', op: 'load', delta: 14 },
              { k: 'stat', stat: 'happiness', delta: -8 },
            ],
          },
        ],
      },
      {
        id: 'leverage',
        label: 'Use it to negotiate where you are',
        hint: 'mixed',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 52,
            label: 'They matched it',
            narrative: 'A raise you would not have received by asking nicely.',
            tone: 'positive',
            effects: [
              { k: 'salary', mode: 'mult', value: 1.16 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: 4 },
            ],
          },
          {
            weight: 48,
            label: 'It backfired',
            narrative: 'They now know you are looking, and they did not match it.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'adjust', field: 'jobSecurity', delta: -16 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: -8 },
              { k: 'stat', stat: 'stress', delta: 8 },
            ],
          },
        ],
      },
      {
        id: 'ignore',
        label: 'Not interested',
        hint: 'safe',
        outcomes: [
          { weight: 1, label: 'Declined', narrative: 'You like where you are. Worth noting, and rare.', tone: 'neutral', effects: [{ k: 'stat', stat: 'happiness', delta: 3 }] },
        ],
      },
    ],
  },
  {
    id: 'opportunity.introduction',
    title: 'An introduction',
    description: 'Someone put your name in a room you were not in.',
    category: 'opportunity',
    pools: ['pool.opportunity'],
    weight: 3,
    choices: [
      {
        id: 'follow',
        label: 'Follow it up immediately',
        hint: 'safe',
        tags: ['social', 'ambition'],
        outcomes: [
          {
            weight: 72,
            label: 'It led somewhere',
            narrative: 'One coffee, one project, and a step change in what you are considered for.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'adjust', field: 'reputation', delta: 12 },
              { k: 'career', op: 'adjust', field: 'performance', delta: 6 },
              { k: 'salary', mode: 'mult', value: 1.08 },
              { k: 'stat', stat: 'purpose', delta: 5 },
            ],
          },
          {
            weight: 28,
            label: 'Pleasant and pointless',
            narrative: 'A nice conversation that produced nothing. Most of them are.',
            tone: 'neutral',
            effects: [{ k: 'skill', skill: 'social', delta: 3 }],
          },
        ],
      },
      {
        id: 'delay',
        label: 'Reply next week',
        hint: 'risky',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'Replied too late',
            narrative: 'Eleven days later. The moment had a shelf life.',
            tone: 'negative',
            effects: [
              { k: 'counter', name: 'missedOpportunities', delta: 1 },
              { k: 'habit', habit: 'procrastination', delta: 5 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'opportunity.luckyBreak',
    title: 'Good timing, nothing more',
    description: 'You were in the right place. You did not engineer it and you do not have to pretend you did.',
    category: 'luck',
    pools: ['pool.luck', 'pool.opportunity'],
    weight: 3,
    cooldown: 10,
    weightMods: [
      { conditions: [{ k: 'num', path: 'socialHealth', op: '>=', value: 62 }], multiplier: 1.5 },
      { conditions: [{ k: 'trait', trait: 'optimism', op: '>=', value: 60 }], multiplier: 1.2 },
    ],
    automatic: true,
    choices: [
      {
        id: 'take',
        label: 'Take the luck',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'A break',
            narrative: 'Fortune is real, unevenly distributed, and pointless to refuse.',
            tone: 'positive',
            effects: [
              { k: 'salary', mode: 'mult', value: 1.12 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: 8 },
              { k: 'stat', stat: 'happiness', delta: 8 },
              { k: 'counter', name: 'luckyBreaks', delta: 1 },
              { k: 'memory', title: 'A lucky break', tone: 'positive', category: 'luck', weight: 5 },
            ],
          },
        ],
      },
    ],
  },
];

export const INERTIA_EVENTS: GameEvent[] = [
  {
    id: 'inertia.theList',
    title: 'The list',
    description: 'Several things you have decided to do and not done. They are not getting easier.',
    category: 'inertia',
    pools: ['pool.inertia', 'pool.stress', 'pool.regret', 'pool.leisure'],
    weight: 7,
    cooldown: 3,
    requires: [{ k: 'num', path: 'intentionCount', op: '>=', value: 2 }],
    weightMods: [{ conditions: [{ k: 'num', path: 'inertiaPressure', op: '>=', value: 55 }], multiplier: 1.8 }],
    choices: [
      {
        id: 'oneThing',
        label: 'Do exactly one of them',
        detail: 'Not all. One.',
        hint: 'safe',
        tags: ['discipline'],
        outcomes: [
          {
            weight: 1,
            label: 'Cleared one',
            narrative: 'The trick was never doing all of it. It was doing any of it.',
            tone: 'positive',
            effects: [
              { k: 'inertia', op: 'pressure', delta: -18 },
              { k: 'habit', habit: 'procrastination', delta: -10 },
              { k: 'stat', stat: 'purpose', delta: 7 },
              { k: 'stat', stat: 'stress', delta: -8 },
              { k: 'counter', name: 'intentionsCleared', delta: 1 },
            ],
          },
        ],
      },
      {
        id: 'overhaul',
        label: 'Try to do all of them',
        hint: 'risky',
        tags: ['ambition'],
        outcomes: [
          {
            weight: 32,
            label: 'A genuine reset',
            narrative: 'A rare and slightly manic month in which you actually did the lot.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'trait', trait: 'discipline', op: '>=', value: 68 }], multiplier: 2.2 },
              { conditions: [{ k: 'num', path: 'energy', op: '>=', value: 65 }], multiplier: 1.6 },
            ],
            effects: [
              { k: 'inertia', op: 'pressure', delta: -40 },
              { k: 'habit', habit: 'procrastination', delta: -18 },
              { k: 'stat', stat: 'purpose', delta: 14 },
              { k: 'stat', stat: 'energy', delta: -10 },
              { k: 'counter', name: 'intentionsCleared', delta: 3 },
            ],
          },
          {
            weight: 68,
            label: 'Collapsed by week three',
            narrative: 'Ambitious, unsustainable, and abandoned in a manner that made everything feel worse.',
            tone: 'negative',
            effects: [
              { k: 'inertia', op: 'pressure', delta: 8 },
              { k: 'stat', stat: 'energy', delta: -8 },
              { k: 'trait', trait: 'optimism', delta: -3 },
              { k: 'habit', habit: 'procrastination', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'nothing',
        label: 'Not this year',
        hint: 'risky',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'The list grew',
            narrative: 'You reread it, felt briefly bad, and closed the notebook.',
            tone: 'negative',
            effects: [
              { k: 'inertia', op: 'pressure', delta: 10 },
              { k: 'habit', habit: 'procrastination', delta: 6 },
              { k: 'stat', stat: 'happiness', delta: -4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'inertia.smallStart',
    title: 'Start absurdly small',
    description: 'Not the whole plan. The first five minutes of it.',
    category: 'inertia',
    pools: ['pool.inertia', 'pool.stress', 'pool.leisure'],
    weight: 5,
    cooldown: 4,
    requires: [{ k: 'num', path: 'inertiaPressure', op: '>=', value: 40 }],
    choices: [
      {
        id: 'tiny',
        label: 'Do the five minutes',
        hint: 'safe',
        tags: ['discipline'],
        outcomes: [
          {
            weight: 78,
            label: 'It started',
            narrative: 'Five minutes became twenty, which became a habit, which became the thing.',
            tone: 'positive',
            effects: [
              { k: 'inertia', op: 'pressure', delta: -14 },
              { k: 'habit', habit: 'procrastination', delta: -8 },
              { k: 'trait', trait: 'discipline', delta: 4 },
            ],
          },
          {
            weight: 22,
            label: 'Five minutes and no more',
            narrative: 'Technically you started. Technically.',
            tone: 'neutral',
            effects: [{ k: 'inertia', op: 'pressure', delta: -4 }],
          },
        ],
      },
      {
        id: 'plan',
        label: 'Make a proper plan first',
        detail: 'Planning is the most comfortable form of not starting.',
        hint: 'risky',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'Planned instead',
            narrative: 'An excellent plan. Detailed, colour-coded, and never begun.',
            tone: 'negative',
            effects: [
              { k: 'inertia', op: 'pressure', delta: 7 },
              { k: 'habit', habit: 'procrastination', delta: 5 },
            ],
          },
        ],
      },
    ],
  },
];
